<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAlert } from 'dashboard/composables';
import { useCurrency } from 'dashboard/composables/useCurrency';
import KanbanAPI from 'dashboard/api/kanban';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ContactInfo from '../../../contact/ContactInfo.vue';
import DealPhones from './DealPhones.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import CustomAttributes from '../../../customAttributes/CustomAttributes.vue';

const props = defineProps({
  conversation: {
    type: Object,
    default: () => ({}),
  },
  mapping: {
    type: Object,
    default: () => ({}),
  },
  funnel: {
    type: Object,
    default: () => ({}),
  },
  // Grupo a renderizar quando usado em abas ('resumo' | 'contato' | 'all').
  section: {
    type: String,
    default: 'all',
  },
});

const emit = defineEmits(['update']);

const showSection = key => props.section === 'all' || props.section === key;

const { t } = useI18n();
const { currency } = useCurrency();

const sender = computed(() => props.conversation?.meta?.sender || {});

// Valor exibido — prioriza o valor salvo localmente (atualização imediata
// após salvar, sem esperar o reload) e cai para o mapping/conversa.
const localValue = ref(null);
const dealValue = computed(() => {
  const raw =
    localValue.value != null
      ? localValue.value
      : (props.mapping?.deal_value ?? props.conversation?.deal_value);
  const value = typeof raw === 'string' ? parseFloat(raw) : raw;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency.value,
  }).format(value || 0);
});

// Edição inline do valor do negócio (movida do cabeçalho para cá — fonte única)
const isEditingValue = ref(false);
const editedValue = ref('');
const isSavingValue = ref(false);

const startEditValue = () => {
  editedValue.value =
    localValue.value != null
      ? localValue.value
      : (props.mapping?.deal_value ?? props.conversation?.deal_value ?? '');
  isEditingValue.value = true;
};

const cancelEditValue = () => {
  isEditingValue.value = false;
};

const saveValue = async () => {
  if (!props.funnel?.id || !props.conversation?.id) return;
  isSavingValue.value = true;
  try {
    await KanbanAPI.updateDealValue(
      props.funnel.id,
      props.conversation.id,
      parseFloat(editedValue.value) || 0
    );
    localValue.value = parseFloat(editedValue.value) || 0;
    useAlert(t('KANBAN.DEAL.VALUE_UPDATED'));
    isEditingValue.value = false;
    emit('update');
  } catch (error) {
    useAlert(t('KANBAN.DEAL.VALUE_UPDATE_ERROR'));
  } finally {
    isSavingValue.value = false;
  }
};

const intentScore = computed(() => {
  const raw = sender.value?.custom_attributes?.lia_intent_score;
  if (raw === null || raw === undefined || raw === '') return null;
  const score = parseInt(raw, 10);
  return Number.isNaN(score) ? null : score;
});

// Por que a IA deu essa temperatura/score — motivo + trechos verbatim do cliente.
const intentReasoning = computed(
  () => sender.value?.custom_attributes?.lia_intent_reasoning || ''
);
const intentSignals = computed(() => {
  const raw = sender.value?.custom_attributes?.lia_intent_signals;
  return Array.isArray(raw) ? raw.filter(Boolean) : [];
});

const probability = computed(() => {
  const raw = props.mapping?.metadata?.probability;
  if (raw !== null && raw !== undefined && raw !== '') {
    const value = typeof raw === 'string' ? parseFloat(raw) : raw;
    if (!Number.isNaN(value)) return value;
  }

  // Fall back to the funnel stage's default probability (mirrors
  // DealDetailsSidebar's rule) so the bar still shows something meaningful
  // when this specific deal never had its probability overridden.
  const stageConfig = props.funnel?.stages?.[props.mapping?.stage_name];
  return stageConfig?.default_probability ?? null;
});

const probabilityBarClass = computed(() => {
  if (probability.value >= 70) return 'bg-n-teal-9';
  if (probability.value >= 40) return 'bg-n-amber-9';
  return 'bg-n-ruby-9';
});

const expectedCloseDate = computed(
  () =>
    props.mapping?.expected_close_date ||
    props.conversation?.expected_close_date ||
    ''
);

const formattedExpectedCloseDate = computed(() => {
  if (!expectedCloseDate.value) return '';
  const date = new Date(expectedCloseDate.value);
  if (Number.isNaN(date.getTime())) return expectedCloseDate.value;
  return date.toLocaleDateString('pt-BR');
});

// Inline edit of the expected close date — the only field this panel writes
// to directly (value/stage/won/lost live in DealHeader). Reuses the same
// KanbanAPI.updateDealMetadata endpoint DealHeader's reopenDeal() calls.
const isEditingCloseDate = ref(false);
const editedCloseDate = ref('');
const isSavingCloseDate = ref(false);

const startEditCloseDate = () => {
  editedCloseDate.value = expectedCloseDate.value || '';
  isEditingCloseDate.value = true;
};

const cancelEditCloseDate = () => {
  isEditingCloseDate.value = false;
};

const saveCloseDate = async () => {
  if (!props.funnel?.id || !props.conversation?.id) return;
  isSavingCloseDate.value = true;
  try {
    await KanbanAPI.updateDealMetadata(props.funnel.id, props.conversation.id, {
      expected_close_date: editedCloseDate.value,
    });
    useAlert(t('KANBAN.DEAL.EXPECTED_CLOSE_UPDATED'));
    isEditingCloseDate.value = false;
    emit('update');
  } catch (error) {
    useAlert(t('KANBAN.DEAL.EXPECTED_CLOSE_UPDATE_ERROR'));
  } finally {
    isSavingCloseDate.value = false;
  }
};

const TEMPERATURE_CONFIGS = {
  hot: {
    emoji: '🔥',
    labelKey: 'DEAL_RECORD.ABOUT.TEMPERATURE.HOT',
    klass: 'bg-n-ruby-3 text-n-ruby-11',
  },
  warm: {
    emoji: '☀️',
    labelKey: 'DEAL_RECORD.ABOUT.TEMPERATURE.WARM',
    klass: 'bg-n-amber-3 text-n-amber-11',
  },
  cold: {
    emoji: '❄️',
    labelKey: 'DEAL_RECORD.ABOUT.TEMPERATURE.COLD',
    klass: 'bg-n-blue-3 text-n-blue-11',
  },
  frozen: {
    emoji: '🧊',
    labelKey: 'DEAL_RECORD.ABOUT.TEMPERATURE.FROZEN',
    klass: 'bg-n-slate-3 text-n-slate-11',
  },
};

const temperatureConfig = computed(
  () => TEMPERATURE_CONFIGS[props.mapping?.lead_temperature] || null
);

const assignee = computed(() => props.conversation?.meta?.assignee || null);

// Normalizes date values coming either as Unix seconds (numbers, the shape
// conversation.created_at comes in) or ISO8601 strings — mirrors
// DealInsights.vue's normalizeDate helper.
const normalizeDate = value => {
  if (!value) return null;
  if (typeof value === 'number' && value < 4102444800) {
    return new Date(value * 1000);
  }
  return new Date(value);
};

const createdAtLabel = computed(() => {
  const date = normalizeDate(props.conversation?.created_at);
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('pt-BR');
});
</script>

<template>
  <div class="flex flex-col w-full gap-3 p-4">
    <CardLayout v-if="showSection('resumo')">
      <h3
        class="flex items-center gap-1.5 text-[13px] font-semibold text-n-slate-12"
      >
        <span class="i-lucide-clipboard-list text-sm text-n-slate-10" />
        {{ t('DEAL_RECORD.ABOUT.SUMMARY_TITLE') }}
      </h3>

      <div class="flex flex-col w-full divide-y divide-n-weak">
        <div
          class="flex items-center justify-between w-full gap-2 py-1 first:pt-0 last:pb-0"
        >
          <span class="text-xs text-n-slate-11 shrink-0">
            {{ t('DEAL_RECORD.ABOUT.VALUE') }}
          </span>
          <div v-if="isEditingValue" class="flex items-center gap-1.5">
            <Input
              v-model="editedValue"
              type="number"
              size="sm"
              class="w-28 min-w-0"
              @enter="saveValue"
            />
            <Button
              size="xs"
              icon="i-lucide-check"
              :is-loading="isSavingValue"
              :aria-label="t('KANBAN.DEAL.SAVE')"
              @click="saveValue"
            />
            <Button
              size="xs"
              variant="faded"
              color="slate"
              icon="i-lucide-x"
              :aria-label="t('KANBAN.DEAL.CANCEL')"
              @click="cancelEditValue"
            />
          </div>
          <button
            v-else
            type="button"
            class="text-sm font-semibold text-n-slate-12 tabular-nums hover:underline"
            @click="startEditValue"
          >
            {{ dealValue }}
          </button>
        </div>

        <div
          v-if="intentScore !== null"
          class="flex items-center justify-between w-full py-1 first:pt-0 last:pb-0"
        >
          <span class="text-xs text-n-slate-11">
            {{ t('DEAL_RECORD.ABOUT.IA_SCORE') }}
          </span>
          <span
            class="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium bg-n-iris-3 text-n-iris-11"
          >
            <span class="i-lucide-sparkles text-xs" />
            {{ intentScore }}
          </span>
        </div>

        <div
          v-if="probability !== null"
          class="flex flex-col w-full gap-1 py-1 first:pt-0 last:pb-0"
        >
          <div class="flex items-center justify-between w-full">
            <span class="text-xs text-n-slate-11">
              {{ t('DEAL_RECORD.ABOUT.PROBABILITY') }}
            </span>
            <span class="text-xs font-medium text-n-slate-12">
              {{ `${probability}%` }}
            </span>
          </div>
          <div class="h-1.5 w-full overflow-hidden rounded-full bg-n-alpha-2">
            <div
              class="h-full rounded-full transition-all"
              :class="probabilityBarClass"
              :style="{ width: `${probability}%` }"
            />
          </div>
        </div>

        <div class="flex flex-col w-full gap-1.5 py-1 first:pt-0 last:pb-0">
          <div class="flex items-center justify-between w-full">
            <span class="text-xs text-n-slate-11">
              {{ t('DEAL_RECORD.ABOUT.EXPECTED_CLOSE_DATE') }}
            </span>
            <template v-if="!isEditingCloseDate">
              <button
                v-if="formattedExpectedCloseDate"
                type="button"
                class="text-xs font-medium text-n-slate-12 hover:underline"
                @click="startEditCloseDate"
              >
                {{ formattedExpectedCloseDate }}
              </button>
              <Button
                v-else
                size="sm"
                variant="faded"
                color="slate"
                :label="t('KANBAN.DEAL.DETAILS.SET_DATE')"
                @click="startEditCloseDate"
              />
            </template>
          </div>
          <div v-if="isEditingCloseDate" class="flex items-center gap-1.5">
            <Input
              v-model="editedCloseDate"
              type="date"
              size="sm"
              class="flex-1 min-w-0"
              @enter="saveCloseDate"
            />
            <Button
              size="xs"
              icon="i-lucide-check"
              :aria-label="t('KANBAN.DEAL.SAVE')"
              :is-loading="isSavingCloseDate"
              @click="saveCloseDate"
            />
            <Button
              size="xs"
              variant="faded"
              color="slate"
              icon="i-lucide-x"
              :aria-label="t('KANBAN.DEAL.CANCEL')"
              @click="cancelEditCloseDate"
            />
          </div>
        </div>

        <div
          v-if="temperatureConfig"
          class="flex items-center justify-between w-full py-1 first:pt-0 last:pb-0"
        >
          <span class="text-xs text-n-slate-11">
            {{ t('DEAL_RECORD.ABOUT.TEMPERATURE_LABEL') }}
          </span>
          <span
            class="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium"
            :class="temperatureConfig.klass"
          >
            <span>{{ temperatureConfig.emoji }}</span>
            {{ t(temperatureConfig.labelKey) }}
          </span>
        </div>

        <!-- Por que a IA classificou assim (motivo + trechos do cliente) -->
        <div
          v-if="intentReasoning || intentSignals.length"
          class="w-full rounded-lg border border-n-weak bg-n-alpha-1 p-2.5"
        >
          <div
            class="flex items-center gap-1.5 text-xs font-medium text-n-slate-11"
          >
            <span class="i-lucide-sparkles size-3.5 text-n-brand" />
            {{ t('DEAL_RECORD.ABOUT.INTENT_WHY') }}
          </div>
          <p v-if="intentReasoning" class="mt-1 text-xs text-n-slate-12">
            {{ intentReasoning }}
          </p>
          <ul v-if="intentSignals.length" class="mt-1.5 flex flex-col gap-1">
            <li
              v-for="(signal, idx) in intentSignals"
              :key="idx"
              class="flex items-start gap-1.5 text-xs text-n-slate-11"
            >
              <span
                class="i-lucide-quote mt-0.5 size-3 shrink-0 text-n-slate-9"
              />
              <span class="italic">{{ signal }}</span>
            </li>
          </ul>
        </div>

        <div
          v-if="assignee"
          class="flex items-center justify-between w-full py-1 first:pt-0 last:pb-0"
        >
          <span class="text-xs text-n-slate-11">
            {{ t('DEAL_RECORD.ABOUT.OWNER') }}
          </span>
          <div class="flex items-center gap-2">
            <Avatar
              :src="assignee.thumbnail"
              :name="assignee.name || ''"
              :size="20"
              rounded-full
            />
            <span class="text-xs font-medium text-n-slate-12">
              {{ assignee.name }}
            </span>
          </div>
        </div>

        <div
          v-if="createdAtLabel"
          class="flex items-center justify-between w-full py-1 first:pt-0 last:pb-0"
        >
          <span class="text-xs text-n-slate-11">
            {{ t('KANBAN.DEAL.DETAILS.CREATED_AT') }}
          </span>
          <span class="text-xs font-medium text-n-slate-12">
            {{ createdAtLabel }}
          </span>
        </div>
      </div>
    </CardLayout>

    <CardLayout v-if="showSection('contato')">
      <h3
        class="flex items-center gap-1.5 text-[13px] font-semibold text-n-slate-12"
      >
        <span class="i-lucide-user text-sm text-n-slate-10" />
        {{ t('DEAL_RECORD.ABOUT.CONTACT_TITLE') }}
      </h3>
      <ContactInfo :contact="sender" />
    </CardLayout>

    <DealPhones
      v-if="showSection('contato')"
      :contact="sender"
      @update="emit('update')"
    />

    <CardLayout v-if="showSection('contato')">
      <h3
        class="flex items-center gap-1.5 text-[13px] font-semibold text-n-slate-12"
      >
        <span class="i-lucide-align-left text-sm text-n-slate-10" />
        {{ t('DEAL_RECORD.ABOUT.DETAILS_TITLE') }}
      </h3>
      <ContactLabels :contact-id="sender.id" />
      <CustomAttributes
        attribute-type="contact_attribute"
        attribute-from="conversation_contact_panel"
        :contact-id="sender.id"
      />
    </CardLayout>
  </div>
</template>
